/**
 * Banking Overview — the banking_use_case_v1 landing surface (D-BANK-UX-03).
 *
 * The inventory is a good operational workspace and a poor front door: it
 * opens on a filter bar and a grid of records whose vocabulary — scope
 * pending, not mapped, materiality — only reads to someone who already holds
 * the model. This page carries the model, so the inventory does not have to.
 *
 *   ARC defines what should be true.
 *   Compass shows what is known, missing, or contradicted.
 *   The bank makes the decision.
 *
 * Composition:
 *  1. masthead — the thesis, stated once, in the Library's own visual voice
 *     so the two surfaces read as one product;
 *  2. the defensibility chain — the four stages from running AI to authorized
 *     decision, each carrying this organization's real numbers;
 *  3. your next decision — ONE state-derived action, so a returning user is
 *     told the one thing they owe rather than greeted;
 *  4. continue work / explore — back into the workspace, out to orientation.
 *
 * Every number comes from endpoints the banking surfaces already serve. This
 * view computes no finding, no score, and no materiality tier: absence renders
 * as absence, an unreadable read renders as unknown, and a stage the engine
 * cannot yet produce says so rather than showing an empty result.
 *
 * ARC IP boundary: mapping-state identifiers and generic product labels only.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import {
  nextDecision,
  summarizeProgram,
  type AssessmentSummary,
  type ProgramSummary,
} from './overviewModel';
import { ARC_SCOPE_STATES, type ImportSummary, type UseCaseSummary } from './useCaseVocabulary';
import './banking-use-cases.css';
import './banking-overview.css';

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'loaded'; useCases: UseCaseSummary[] };

interface ListEnvelope {
  data?: { useCases?: UseCaseSummary[] };
}
interface StatesEnvelope {
  data?: { states?: Record<string, string> };
}
interface ImportsEnvelope {
  data?: { imports?: ImportSummary[] };
}
interface AssessmentEnvelope {
  data?: { summary?: AssessmentSummary };
}

/** A chain stage: the question it answers, and what this bank can answer today. */
function Stage(props: {
  id: string;
  step: string;
  title: string;
  question: string;
  value: string;
  valueLabel: string;
  secondary: string;
  muted?: boolean;
}): React.ReactElement {
  const { id, step, title, question, value, valueLabel, secondary, muted } = props;
  return (
    <li className={muted ? 'ovw-stage is-muted' : 'ovw-stage'} data-testid={`chain-stage-${id}`}>
      <span className="ovw-stage-step">{step}</span>
      <h3 className="ovw-stage-title">{title}</h3>
      <p className="ovw-stage-q">{question}</p>
      <p className="ovw-stage-value">
        <span className="ovw-stage-n">{value}</span>
        <span className="ovw-stage-n-label">{valueLabel}</span>
      </p>
      <p className="ovw-stage-secondary">{secondary}</p>
    </li>
  );
}

export const BankingOverview: React.FC = () => {
  const { get } = useApiClient();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [arcStates, setArcStates] = useState<Record<string, string>>({});
  const [arcStatesKnown, setArcStatesKnown] = useState(false);
  const [latestImport, setLatestImport] = useState<ImportSummary | null>(null);
  const [assessment, setAssessment] = useState<AssessmentSummary | null>(null);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const list = await get<ListEnvelope>('/api/use-cases');
      setState({ phase: 'loaded', useCases: list.data?.useCases ?? [] });
    } catch {
      setState({ phase: 'error' });
      return;
    }
    // A failed mapping-state read leaves scope UNKNOWN, never zero — the whole
    // page then refuses to state a scope position (see nextDecision).
    try {
      const states = await get<StatesEnvelope>('/api/use-cases/arc-mapping-states');
      setArcStates(states.data?.states ?? {});
      setArcStatesKnown(true);
    } catch {
      setArcStates({});
      setArcStatesKnown(false);
    }
    // Partial failure renders partially: no import history simply means no
    // import row in Continue work.
    try {
      const imports = await get<ImportsEnvelope>('/api/use-cases/imports');
      setLatestImport(imports.data?.imports?.[0] ?? null);
    } catch {
      setLatestImport(null);
    }
    // A failed assessment read leaves the last two stages UNKNOWN, never zero
    // — the same rule the scope read already follows.
    try {
      const summary = await get<AssessmentEnvelope>('/api/use-cases/assessment-summary');
      setAssessment(summary.data?.summary ?? null);
    } catch {
      setAssessment(null);
    }
  }, [get]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary: ProgramSummary | null = useMemo(
    () =>
      state.phase === 'loaded'
        ? summarizeProgram({
            useCases: state.useCases,
            arcStates,
            arcStatesKnown,
            latestImport,
            assessment,
          })
        : null,
    [state, arcStates, arcStatesKnown, latestImport, assessment]
  );

  const decision = summary ? nextDecision(summary) : null;
  const n = (value: number | null): string => (value === null ? '—' : String(value));

  return (
    <div className="banking-page banking-overview">
      <header className="ovw-masthead" data-testid="overview-masthead">
        <p className="ovw-mast-top">Compass for Banking · AI Risk &amp; Compliance</p>
        <h1 className="ovw-mast-thesis">From AI use case to defensible operating decision.</h1>
        <p className="ovw-mast-sub">
          Compass maps each material AI use case to the ARC banking control baseline, connects the
          bank&apos;s data and governance evidence, and preserves the rationale for an accountable
          human decision.
        </p>
        <div className="ovw-labor" data-testid="overview-division-of-labor">
          <span className="ovw-labor-part">
            <b>ARC</b> defines what should be true.
          </span>
          <span className="ovw-labor-part">
            <b>Compass</b> shows what is known, missing, or contradicted.
          </span>
          <span className="ovw-labor-part">
            <b>The bank</b> makes the decision.
          </span>
        </div>
      </header>

      {state.phase === 'loading' && (
        <p className="ovw-loading" aria-busy="true">
          Reading the bank&apos;s AI inventory…
        </p>
      )}

      {state.phase === 'error' && (
        <div className="ovw-error" role="alert">
          <p>The AI use-case inventory could not be read, so this page can state nothing about it.</p>
          <button type="button" className="banking-btn banking-btn-secondary" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {summary && decision && (
        <>
          <section className="ovw-chain-wrap" aria-labelledby="ovw-chain-h">
            <h2 className="ovw-section-h" id="ovw-chain-h">
              The defensibility chain
            </h2>
            <ol className="ovw-chain" data-testid="defensibility-chain">
              <Stage
                id="use-case"
                step="AI use case"
                title="What is running?"
                question="The bank's own record of where AI operates, and who is accountable for it."
                value={String(summary.registered)}
                valueLabel={summary.registered === 1 ? 'registered' : 'registered'}
                secondary={`${summary.inProduction} in production`}
              />
              <Stage
                id="arc-scope"
                step="ARC scope"
                title="What should apply?"
                question="The risks, controls, and tests ARC requires of a use case like this one."
                value={n(summary.scoped)}
                valueLabel="scoped"
                secondary={
                  summary.scopeKnown
                    ? `${summary.needScope} need scope · ${summary.validated} validated`
                    : 'Scope states unavailable — not a measurement of zero'
                }
                muted={!summary.scopeKnown}
              />
              <Stage
                id="evidence"
                step="Evidence & findings"
                title="What does the evidence show?"
                question="What the bank's data and governance record prove, contradict, or leave open."
                value={n(summary.subjectsAssessed)}
                valueLabel={summary.assessmentKnown ? 'assessed' : 'unknown'}
                secondary={
                  !summary.assessmentKnown
                    ? 'The assessment state could not be read — this is not a measurement of zero.'
                    : (summary.subjectsAssessed ?? 0) > 0
                      ? 'Each run answers every in-scope test, and states the ones nothing could answer rather than scoring them.'
                      : 'No assessment has run yet. Run one from a validated use case.'
                }
                muted={!summary.assessmentKnown || (summary.subjectsAssessed ?? 0) === 0}
              />
              <Stage
                id="decision"
                step="Bank decision"
                title="What is authorized?"
                question="The operating decision, its rationale, and the person who owns it."
                value={n(summary.runsWithDecision)}
                valueLabel={
                  !summary.assessmentKnown
                    ? 'unknown'
                    : (summary.runsWithDecision ?? 0) > 0
                      ? 'recorded'
                      : 'none recorded'
                }
                secondary="Human-owned and retained. Compass preserves the rationale; it never issues the decision."
                muted={!summary.assessmentKnown || (summary.runsWithDecision ?? 0) === 0}
              />
            </ol>
          </section>

          <section className="ovw-next" data-testid="next-decision" aria-labelledby="ovw-next-h">
            <p className="banking-eyebrow">Your next decision</p>
            <h2 className="ovw-next-h" id="ovw-next-h">
              {decision.headline}
            </h2>
            <p className="ovw-next-detail">{decision.detail}</p>
            {decision.action ? (
              <Link
                className="banking-btn banking-btn-primary ovw-primary"
                data-testid="overview-primary-action"
                to={decision.action.to}
              >
                {decision.action.label}
              </Link>
            ) : (
              decision.inDevelopment && (
                <p className="ovw-soon">Assessment engine · in development</p>
              )
            )}
          </section>

          <div className="ovw-columns">
            <section className="ovw-col" data-testid="overview-continue" aria-labelledby="ovw-cont-h">
              <h2 className="ovw-section-h" id="ovw-cont-h">
                Continue work
              </h2>
              {summary.awaitingValidation !== null && summary.awaitingValidation > 0 && (
                <Link className="ovw-row" to="/use-cases?arc=awaiting">
                  <span className="ovw-row-n">{summary.awaitingValidation}</span>
                  <span>
                    {summary.awaitingValidation === 1 ? 'mapping awaits' : 'mappings await'}{' '}
                    validation
                  </span>
                </Link>
              )}
              {summary.importIssues && (
                <Link className="ovw-row" to="/use-cases">
                  <span className="ovw-row-n">{summary.importIssues.rejected}</span>
                  <span>
                    rejected {summary.importIssues.rejected === 1 ? 'row' : 'rows'} in{' '}
                    {summary.importIssues.filename}
                  </span>
                </Link>
              )}
              {summary.recentlyChanged.length > 0 && (
                <>
                  <p className="ovw-col-sub">Recently changed</p>
                  <ul className="ovw-recent">
                    {summary.recentlyChanged.map((uc) => (
                      <li key={uc.id}>
                        <Link to={`/use-cases/${uc.id}`}>
                          <span className="ovw-recent-ref">{uc.useCaseRef}</span>
                          <span className="ovw-recent-name">{uc.name}</span>
                        </Link>
                        <span className="ovw-recent-state">
                          {ARC_SCOPE_STATES[arcStates[uc.id]]?.label ??
                            (arcStatesKnown ? ARC_SCOPE_STATES.unmapped.label : '—')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {summary.registered === 0 && (
                <p className="ovw-col-empty">
                  Nothing registered yet — the inventory is where this program starts.
                </p>
              )}
            </section>

            <nav className="ovw-col" data-testid="overview-explore" aria-labelledby="ovw-exp-h">
              <h2 className="ovw-section-h" id="ovw-exp-h">
                Explore
              </h2>
              <Link className="ovw-row" to="/use-cases">
                <span>AI Use Case Inventory</span>
                <span className="ovw-row-note">Every use case the bank has registered</span>
              </Link>
              <Link className="ovw-row" to="/arc-library">
                <span>ARC Library</span>
                <span className="ovw-row-note">
                  The control baseline itself — risk to control to test to evidence
                </span>
              </Link>
              <Link className="ovw-row" to="/home">
                <span>Datasets</span>
                <span className="ovw-row-note">
                  The data underneath — quality, stability, and fairness screens
                </span>
              </Link>
              <Link className="ovw-row" to="/settings">
                <span>Administration</span>
                <span className="ovw-row-note">Members, access, and organization settings</span>
              </Link>
            </nav>
          </div>
        </>
      )}
    </div>
  );
};

export default BankingOverview;
